import React, { useEffect, useRef } from 'react';
import { useFormContext } from 'react-hook-form';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import { ENDPOINTS } from 'features/command-center/constants';
import { CALL_TYPES } from 'features/daily-plan/constants';
import strings from 'strings';
import { When } from 'UI/components/atoms/When';
import AutocompleteSelect from 'UI/components/molecules/AutocompleteSelect';
import { DateFormats } from 'UI/constants/defaults';
import { idOptionSelected, REQUIRED_VALIDATION, toLocalTime } from 'UI/utils';

import { SendoutSummary } from '../shared/SendoutSummary';

import { FORM_FIELDS_MAP } from './CallTask.constants';
import { useStyles } from './CallTask.styles';
import { buildSendoutDetails } from './CallTask.utils';

const { form: COPIES } = strings.dailyPlan.drawer;

const InterviewOptionsRender = option => (
  <div>
    <Typography variant="body1">
      {option?.jobOrderTitle} / {option?.companyName}
    </Typography>

    <Typography variant="body2" color="textSecondary">
      {option?.hiringAuthorityName} / {option?.candidateName} /{' '}
      {option?.interviewDate &&
        toLocalTime(option?.interviewDate).format(DateFormats.MonthDayYearTime)}
    </Typography>
  </div>
);

const validateAtLeastOneContact = getValues => {
  const formValues = getValues();
  const atLeastOneContact =
    formValues[FORM_FIELDS_MAP.IsCandidateSelected.key] ||
    formValues[FORM_FIELDS_MAP.IsHiringAuthSelected.key];
  return atLeastOneContact || strings.dailyPlan.drawer.form.labels.atLeastOneContact;
};

export const CallTask = ({
  callType,
  initialInterviewSearch,
  isFetchingInterview,
  onSendoutDetailsClick
}) => {
  const classes = useStyles();
  const { errors, register, unregister, watch, setValue, getValues } = useFormContext();
  const formValues = watch();
  const inputRef = useRef(null);

  const taskId = formValues[FORM_FIELDS_MAP.TaskId.key];
  const recruiterId = formValues[FORM_FIELDS_MAP.Recruiter.key]?.id ?? null;

  useEffect(() => {
    const dispatchInputEvent = () => {
      if (initialInterviewSearch && inputRef.current) {
        inputRef.current.focus();
        const nativeInputValueSetter = Object.getOwnPropertyDescriptor(
          window.HTMLInputElement.prototype,
          'value'
        )?.set;

        if (nativeInputValueSetter) {
          nativeInputValueSetter.call(inputRef.current, initialInterviewSearch);

          const event = new Event('input', { bubbles: true });
          inputRef.current.dispatchEvent(event);
        }
      }
    };

    dispatchInputEvent();
  }, [initialInterviewSearch, callType]);

  useEffect(() => {
    register(FORM_FIELDS_MAP.Interview.key, { ...REQUIRED_VALIDATION });
    register(FORM_FIELDS_MAP.IsCandidateSelected.key);
    register(FORM_FIELDS_MAP.IsHiringAuthSelected.key);
    register(FORM_FIELDS_MAP.AtLeastOneContact.key, {
      validate: () => validateAtLeastOneContact(getValues)
    });
    register(FORM_FIELDS_MAP.EntityId.key);
    return () => {
      unregister(FORM_FIELDS_MAP.Interview.key);
      unregister(FORM_FIELDS_MAP.IsCandidateSelected.key);
      unregister(FORM_FIELDS_MAP.IsHiringAuthSelected.key);
      unregister(FORM_FIELDS_MAP.AtLeastOneContact.key);
      unregister(FORM_FIELDS_MAP.EntityId.key);
    };
  }, [register, unregister, getValues]);

  const handleAutocompleteChange = (name, value) => {
    setValue(name, value, true);
    setValue(FORM_FIELDS_MAP.IsCandidateSelected.key, true);
    setValue(FORM_FIELDS_MAP.IsHiringAuthSelected.key, true);
    setValue(FORM_FIELDS_MAP.AtLeastOneContact.key, true);
  };

  const sendoutDetails = buildSendoutDetails(formValues[FORM_FIELDS_MAP.Interview.key]);

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Typography variant="body1" className={classes.fieldLabel}>
          <b>{COPIES.labels.taskDetails}</b>
        </Typography>

        <Typography variant="body2" color="textSecondary" className={classes.fieldLabel}>
          {COPIES.labels.interview}
        </Typography>

        <AutocompleteSelect
          name={FORM_FIELDS_MAP.Interview.key}
          error={!!errors[FORM_FIELDS_MAP.Interview.key]}
          errorText={errors[FORM_FIELDS_MAP.Interview.key]?.message}
          apiVersion={2}
          getOptionLabel={option => `${option?.jobOrderTitle} / ${option?.companyName}`}
          getOptionSelected={idOptionSelected}
          onSelect={handleAutocompleteChange}
          placeholder={strings.dailyPlan.drawer.form.placeholders.selectInterview}
          renderOption={InterviewOptionsRender}
          selectedValue={formValues[FORM_FIELDS_MAP.Interview.key] || null}
          textFieldProps={{ inputRef }}
          typeahead
          typeaheadLimit={50}
          url={
            callType === CALL_TYPES.preparation
              ? ENDPOINTS.preparationCalls
              : ENDPOINTS.debriefingCalls
          }
          disabled={!!taskId}
          typeaheadParams={{ recruiterIds: recruiterId }}
        />
      </Grid>
      <When condition={!!formValues[FORM_FIELDS_MAP.Interview.key] && !!sendoutDetails}>
        <Grid item xs={12}>
          <SendoutSummary
            sendoutDetails={sendoutDetails}
            onSendoutDetailsClick={onSendoutDetailsClick}
            isFetchingInterview={isFetchingInterview}
          />
        </Grid>
      </When>
    </Grid>
  );
};
